import React, {useEffect, useRef, useState} from "react"
import classNames from "classnames"
import Select, {SingleValue} from "react-select"
import {Button} from "../../components/Button/Button"
import ToggleSettings from "../../components/ToggleSettings/ToggleSettings"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {getPaymentProcessorSettings, updateAppConfigPaymentProcessing} from "../../resources/orgSettings"
import {useToasts} from "react-toast-notifications"
import {useAccessToken} from "../../services/auth"
import {stripeMccCodes} from "./stripeMccCodes"
import ReactTooltip from "react-tooltip"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons"
import {GridContainer} from "../../containers/GridContainer/GridContainer"
import Avatar from "../../components/Avatar/Avatar"
import Icon from "../../components/Icon/Icon"
import StripeIcon from "../../components/StripeIcon/StripeIcon"
import Admonition from "../../components/Admonitions/Admonition"
import {ShowMoreParagraph} from "../../components/ShowMoreParagraph/ShowMoreParagraph"

interface MccCodeOption {
	value: string
	label: string
}

const mccCodeOptions: MccCodeOption[] = stripeMccCodes.map((mcc) => ({
	value: mcc.code,
	label: `${mcc.code} - ${mcc.description}`,
}))

function validatePaymentProcessingFields(
	fields: {apiKeySet: boolean; webhookSecretSet: boolean; mccCode: string | null},
	values: {apiKey: string; webhookSecret: string; publishableKey: string}
) {
	return {
		apiKeyError: !fields.apiKeySet && !values.apiKey.trim() ? "Secret/Restricted API key is required" : null,
		webhookSecretError:
			!fields.webhookSecretSet && !values.webhookSecret.trim() ? "Connected accounts webhook secret is required" : null,
		publishableKeyError: !values.publishableKey.trim() ? "Publishable API key is required" : null,
		mccCodeError: !fields.mccCode ? "MCC code is required" : null,
	}
}

const RequiredMark = () => <span className="has-text-danger">*</span>

export interface PaymentProcessingViewProps {
	enabled: boolean
	setEnabled: (value: boolean) => void
	vendor: string
	setVendor: (value: string) => void
	apiKey: string
	setApiKey: (value: string) => void
	apiKeySet: boolean
	webhookSecret: string
	setWebhookSecret: (value: string) => void
	webhookSecretSet: boolean
	yourAccountWebhookSecret: string
	setYourAccountWebhookSecret: (value: string) => void
	yourAccountWebhookSecretSet: boolean
	publishableKey: string
	setPublishableKey: (value: string) => void
	hostname: string
	setHostname: (value: string) => void
	filesHostname: string
	setFilesHostname: (value: string) => void
	connectedAccountLookupUrl: string
	setConnectedAccountLookupUrl: (value: string) => void
	mccCode: string | null
	setMccCode: (value: string | null) => void
	mccCodeError: string | null
	setMccCodeError: (value: string | null) => void
	apiKeyError: string | null
	setApiKeyError: (value: string | null) => void
	webhookSecretError: string | null
	setWebhookSecretError: (value: string | null) => void
	publishableKeyError: string | null
	setPublishableKeyError: (value: string | null) => void
	onSubmit: (e: React.FormEvent) => void
	isLoading: boolean
	error?: string
}

export function PaymentProcessingView({
	enabled,
	setEnabled,
	vendor,
	setVendor,
	apiKey,
	setApiKey,
	apiKeySet,
	webhookSecret,
	setWebhookSecret,
	webhookSecretSet,
	yourAccountWebhookSecret,
	setYourAccountWebhookSecret,
	yourAccountWebhookSecretSet,
	publishableKey,
	setPublishableKey,
	hostname,
	setHostname,
	filesHostname,
	setFilesHostname,
	connectedAccountLookupUrl,
	setConnectedAccountLookupUrl,
	mccCode,
	setMccCode,
	mccCodeError,
	setMccCodeError,
	apiKeyError,
	setApiKeyError,
	webhookSecretError,
	setWebhookSecretError,
	publishableKeyError,
	setPublishableKeyError,
	onSubmit,
	isLoading,
	error,
}: PaymentProcessingViewProps) {
	return (
		<div className="card managed-solutions-settings-card payment-processing-card">
			<div className="card-content">
				<GridContainer gap={"52px"} responsive={true} columns={2}>
					<div>
						<form onSubmit={onSubmit}>
							<fieldset>
								<ToggleSettings
									name="payment-processing-enabled"
									title="Enable Payment Processing"
									description="When disabled, payment processing credentials will be cleared."
									checked={enabled}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEnabled(e.target.checked)}
								/>
								<hr />
								<div className="field">
									<label htmlFor="vendor" className="label">
										Vendor
									</label>
									<div className="select">
										<select
											id="vendor"
											name="vendor"
											value={vendor}
											onChange={(e) => setVendor(e.target.value)}
											disabled={!enabled}
										>
											<option value="rPaymentsProcessorSettings">Stripe</option>
										</select>
									</div>
								</div>
								<hr />
								{vendor === "rPaymentsProcessorSettings" && (
									<div className="payment-processing-provider-header">
										<Avatar name="Stripe" borderRadius={0} size={42}>
											<StripeIcon size={24} />
										</Avatar>
										<div className="payment-processing-provider-info">
											<span className="payment-processing-provider-name">Stripe</span>
											<a
												href="https://dashboard.stripe.com/apikeys"
												rel="noreferrer"
												target="_blank"
												className="payment-processing-provider-link"
											>
												<span>See credentials in Stripe</span>
												<Icon
													icon="maximize-square-2--interface-essential"
													size={12}
													style={{position: "relative", top: "2px", marginLeft: 4}}
												/>
											</a>
										</div>
									</div>
								)}
								<div className="field">
									<label className="label label-with-tooltip">
										Secret/Restricted API Key {enabled && <RequiredMark />}
									</label>
									<div className="control">
										<input
											type="password"
											autoComplete="new-password"
											className={classNames("input", apiKeySet && !apiKey && "is-saved")}
											placeholder={apiKeySet && !apiKey ? "•••••••••••" : "Ex: sk_live_51ABC..."}
											value={apiKey}
											onChange={(e) => {
												setApiKey(e.target.value)
												setApiKeyError(null)
											}}
											disabled={!enabled}
										/>
										{apiKeySet && !apiKey && (
											<p className="help has-text-grey">Leave blank to keep the current value</p>
										)}
										{apiKeyError && <p className="help has-text-danger">{apiKeyError}</p>}
									</div>
								</div>

								<div className="field">
									<label className="label label-with-tooltip">
										Publishable API Key {enabled && <RequiredMark />}
										<span
											className="tooltip-info"
											style={{marginLeft: 4, cursor: "pointer"}}
											data-tip
											data-for="publishable-api-key-tooltip"
										>
											<FontAwesomeIcon icon={faInfoCircle} color="#545558" />
										</span>
										<ReactTooltip
											id="publishable-api-key-tooltip"
											place="right"
											effect="solid"
											className="tooltip-info"
											border={true}
											borderColor="#C4C4C4"
											backgroundColor="#F5F5F5"
											textColor="black"
										>
											Used for Stripe.js client-side initialization (e.g. selfie verification)
										</ReactTooltip>
									</label>
									<div className="control">
										<input
											className="input"
											type="text"
											placeholder="Ex: pk_live_51ABC..."
											value={publishableKey}
											onChange={(e) => {
												setPublishableKey(e.target.value)
												setPublishableKeyError(null)
											}}
											disabled={!enabled}
										/>
										{publishableKeyError && <p className="help has-text-danger">{publishableKeyError}</p>}
									</div>
								</div>

								<div className="field">
									<label className="label label-with-tooltip">
										Connected accounts webhook secret {enabled && <RequiredMark />}
									</label>
									<div className="control">
										<input
											type="password"
											autoComplete="new-password"
											className={classNames("input", webhookSecretSet && !webhookSecret && "is-saved")}
											placeholder={webhookSecretSet && !webhookSecret ? "•••••••••••" : "Ex: whsec_ABC123..."}
											value={webhookSecret}
											onChange={(e) => {
												setWebhookSecret(e.target.value)
												setWebhookSecretError(null)
											}}
											disabled={!enabled}
										/>
										{webhookSecretSet && !webhookSecret && (
											<p className="help has-text-grey">Leave blank to keep the current value</p>
										)}
										{webhookSecretError && <p className="help has-text-danger">{webhookSecretError}</p>}
									</div>
								</div>

								<div className="field">
									<label className="label label-with-tooltip">Your account webhook secret</label>
									<div className="control">
										<input
											type="password"
											autoComplete="new-password"
											className={classNames(
												"input",
												yourAccountWebhookSecretSet && !yourAccountWebhookSecret && "is-saved"
											)}
											placeholder={
												yourAccountWebhookSecretSet && !yourAccountWebhookSecret ? "•••••••••••" : "Ex: whsec_ABC123..."
											}
											value={yourAccountWebhookSecret}
											onChange={(e) => setYourAccountWebhookSecret(e.target.value)}
											disabled={!enabled}
										/>
										{yourAccountWebhookSecretSet && !yourAccountWebhookSecret && (
											<p className="help has-text-grey">Leave blank to keep the current value</p>
										)}
									</div>
								</div>

								<div className="field">
									<label className="label label-with-tooltip">
										Connected account lookup URL
										<button
											type="button"
											className="tooltip-info"
											style={{marginLeft: 4, cursor: "pointer", border: "none", background: "none", padding: 0}}
											data-tip
											data-for="connected-account-lookup-url-tooltip"
											aria-label="URL called to resolve a Stripe connected account ID from a JWT subject"
										>
											<FontAwesomeIcon icon={faInfoCircle} color="#545558" />
										</button>
										<ReactTooltip
											id="connected-account-lookup-url-tooltip"
											place="right"
											effect="solid"
											className="tooltip-info"
											border={true}
											borderColor="#C4C4C4"
											backgroundColor="#F5F5F5"
											textColor="black"
										>
											URL called to resolve a Stripe connected account ID from a JWT subject
										</ReactTooltip>
									</label>
									<div className="control">
										<input
											className="input"
											type="url"
											placeholder="Ex: https://api.example.com/stripe/connected-account"
											value={connectedAccountLookupUrl}
											onChange={(e) => setConnectedAccountLookupUrl(e.target.value)}
											disabled={!enabled}
										/>
									</div>
								</div>

								<div className="field">
									<label className="label">Business profile mcc code {enabled && <RequiredMark />}</label>
									<div className="control">
										<Select<MccCodeOption, false>
											id="mcc-code"
											name="mccCode"
											value={mccCodeOptions.find((opt) => opt.value === mccCode) || null}
											onChange={(option: SingleValue<MccCodeOption>) => {
												setMccCode(option ? option.value : null)
												setMccCodeError(null)
											}}
											options={mccCodeOptions}
											isClearable
											isSearchable
											isDisabled={!enabled}
											placeholder="Select or type to search MCC code..."
											menuPlacement="auto"
										/>
										{mccCodeError && <p className="help has-text-danger">{mccCodeError}</p>}
									</div>
								</div>

								<div className="field">
									<label className="label label-with-tooltip">Hostname</label>
									<div className="control">
										<input
											className="input"
											type="text"
											placeholder="Enter Hostname"
											value={hostname}
											onChange={(e) => setHostname(e.target.value)}
											disabled={!enabled}
										/>
									</div>
								</div>

								<div className="field">
									<label className="label label-with-tooltip">Files Hostname</label>
									<div className="control">
										<input
											className="input"
											type="text"
											placeholder="Enter Files Hostname"
											value={filesHostname}
											onChange={(e) => setFilesHostname(e.target.value)}
											disabled={!enabled}
										/>
									</div>
								</div>
							</fieldset>

							<div className="columns">
								<div className="column mt-4 button-container">
									{error ? (
										<>
											<div className="has-text-danger">{error}</div>
											<Button type="submit" className="button primary" prefixIcon="approve-v-check-validation">
												Save
											</Button>
										</>
									) : isLoading ? (
										<Button className="button primary is-loading">Saving</Button>
									) : (
										<Button type="submit" className="button primary" prefixIcon="approve-v-check-validation">
											Save
										</Button>
									)}
								</div>
							</div>
						</form>
					</div>

					<Admonition
						icon={"lock-square-lock-1--interface-essential"}
						type={"is-security-info"}
						title={"We are committed to your security"}
						message={
							<ShowMoreParagraph
								summary={
									<div className={"payment-processing-security-notice"}>
										<p>
											Unit&apos;s implementation of the least privilege principle is ensured in every access to each
											resource. The access policies for AWS Secrets Manager are also designed based on the same
											principle.
										</p>
									</div>
								}
							>
								<div className={"payment-processing-security-notice"}>
									<p>
										Moreover, any modifications made to the AWS Secrets Manager are executed through Terraform, and such
										modifications require a corresponding change in the code, which subsequently necessitates review and
										approval.
									</p>
									<p>
										In addition, the number of users with admin access is limited, and only those authorized personnel
										can access the AWS Secrets Manager for management and troubleshooting purposes.
									</p>
									<p>
										Additionally, all access activities are subjected to an audit process to maintain accountability and
										transparency.
									</p>
								</div>
							</ShowMoreParagraph>
						}
					/>
				</GridContainer>
			</div>
		</div>
	)
}

export default function PaymentProcessing({refresh}: {refresh: () => void}) {
	const accessToken = useAccessToken()

	const [patchState, patch] = useAsyncResultIdle(updateAppConfigPaymentProcessing)
	const {addToast} = useToasts()

	const [settingsRefreshToken, refreshSettings] = useRefreshToken()
	const settingsAsync = useAsyncResult(
		() => getPaymentProcessorSettings(accessToken),
		[accessToken, settingsRefreshToken]
	)

	const [hydrated, setHydrated] = useState(false)
	const [enabled, setEnabled] = useState<boolean>(false)
	const [vendor, setVendor] = useState<string>("rPaymentsProcessorSettings")

	const [apiKey, setApiKey] = useState<string>("")
	const [apiKeySet, setApiKeySet] = useState<boolean>(false)

	const [webhookSecret, setWebhookSecret] = useState<string>("")
	const [webhookSecretSet, setWebhookSecretSet] = useState<boolean>(false)

	const [yourAccountWebhookSecret, setYourAccountWebhookSecret] = useState<string>("")
	const [yourAccountWebhookSecretSet, setYourAccountWebhookSecretSet] = useState<boolean>(false)

	const [publishableKey, setPublishableKey] = useState<string>("")
	const [hostname, setHostname] = useState<string>("")
	const [filesHostname, setFilesHostname] = useState<string>("")
	const [connectedAccountLookupUrl, setConnectedAccountLookupUrl] = useState<string>("")
	const [mccCode, setMccCode] = useState<string | null>(null)

	const [mccCodeError, setMccCodeError] = useState<string | null>(null)
	const [apiKeyError, setApiKeyError] = useState<string | null>(null)
	const [webhookSecretError, setWebhookSecretError] = useState<string | null>(null)
	const [publishableKeyError, setPublishableKeyError] = useState<string | null>(null)

	const enabledAtSubmitRef = useRef<boolean>(false)

	useEffect(() => {
		settingsAsync.match(
			() => {
				if (hydrated) setHydrated(false)
			},
			(settings) => {
				if (hydrated) return
				setHydrated(true)
				setApiKey("")
				setWebhookSecret("")
				setYourAccountWebhookSecret("")
				if (settings) {
					setEnabled(true)
					setApiKeySet(settings.apiKeySet)
					setWebhookSecretSet(settings.webhookSecretSet)
					setYourAccountWebhookSecretSet(settings.yourAccountWebhookSecretSet ?? false)
					setPublishableKey(settings.publishableKey ?? "")
					setHostname(settings.hostname ?? "")
					setFilesHostname(settings.filesHostname ?? "")
					setConnectedAccountLookupUrl(settings.connectedAccountLookupUrl ?? "")
					setMccCode(settings.mccCodes?.[0] ?? null)
				} else {
					setEnabled(false)
					setApiKeySet(false)
					setWebhookSecretSet(false)
					setYourAccountWebhookSecretSet(false)
					setPublishableKey("")
					setHostname("")
					setFilesHostname("")
					setConnectedAccountLookupUrl("")
					setMccCode(null)
				}
			},
			() => {
				if (!hydrated) setHydrated(true)
			}
		)
	}, [settingsAsync, hydrated])

	const handleSetEnabled = (value: boolean) => {
		setEnabled(value)
		if (!value) {
			setApiKey("")
			setWebhookSecret("")
			setYourAccountWebhookSecret("")
			setPublishableKey("")
			setConnectedAccountLookupUrl("")
			setApiKeyError(null)
			setWebhookSecretError(null)
			setPublishableKeyError(null)
			setMccCodeError(null)
		}
	}

	useEffect(() => {
		if (patchState.isOk()) {
			const message = enabledAtSubmitRef.current
				? "Payment Processing Settings Updated Successfully"
				: "Payment Processing Disabled Successfully"
			addToast(message, {appearance: "success"})
			refreshSettings()
			refresh()
		}
	}, [patchState])

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		enabledAtSubmitRef.current = enabled

		if (!enabled) {
			patch(
				accessToken,
				{
					webhookSecret: null,
					yourAccountWebhookSecret: null,
					apiKey: null,
					publishableKey: null,
					connectedAccountLookupUrl: null,
				},
				vendor
			)
			return
		}

		const {
			apiKeyError: apiKeyErr,
			webhookSecretError: webhookSecretErr,
			publishableKeyError: publishableKeyErr,
			mccCodeError: mccCodeErr,
		} = validatePaymentProcessingFields({apiKeySet, webhookSecretSet, mccCode}, {apiKey, webhookSecret, publishableKey})

		setApiKeyError(apiKeyErr)
		setWebhookSecretError(webhookSecretErr)
		setPublishableKeyError(publishableKeyErr)
		setMccCodeError(mccCodeErr)

		if (apiKeyErr || webhookSecretErr || publishableKeyErr || mccCodeErr) {
			return
		}

		patch(
			accessToken,
			{
				...(apiKey.trim() ? {apiKey: apiKey.trim()} : {}),
				...(webhookSecret.trim() ? {webhookSecret: webhookSecret.trim()} : {}),
				...(yourAccountWebhookSecret.trim() ? {yourAccountWebhookSecret: yourAccountWebhookSecret.trim()} : {}),
				publishableKey: publishableKey.trim(),
				hostname,
				filesHostname,
				connectedAccountLookupUrl: connectedAccountLookupUrl.trim() || null,
				mccCodes: [mccCode!],
			},
			vendor
		)
	}

	const error = patchState.match(
		() => undefined,
		() => undefined,
		() => undefined,
		(err) => err.errors[0]?.title
	)

	const isLoading = patchState.match(
		() => false,
		() => true,
		() => false,
		() => false
	)

	return (
		<PaymentProcessingView
			enabled={enabled}
			setEnabled={handleSetEnabled}
			vendor={vendor}
			setVendor={setVendor}
			apiKey={apiKey}
			setApiKey={setApiKey}
			apiKeySet={apiKeySet}
			webhookSecret={webhookSecret}
			setWebhookSecret={setWebhookSecret}
			webhookSecretSet={webhookSecretSet}
			yourAccountWebhookSecret={yourAccountWebhookSecret}
			setYourAccountWebhookSecret={setYourAccountWebhookSecret}
			yourAccountWebhookSecretSet={yourAccountWebhookSecretSet}
			publishableKey={publishableKey}
			setPublishableKey={setPublishableKey}
			hostname={hostname}
			setHostname={setHostname}
			filesHostname={filesHostname}
			setFilesHostname={setFilesHostname}
			connectedAccountLookupUrl={connectedAccountLookupUrl}
			setConnectedAccountLookupUrl={setConnectedAccountLookupUrl}
			mccCode={mccCode}
			setMccCode={setMccCode}
			mccCodeError={mccCodeError}
			setMccCodeError={setMccCodeError}
			apiKeyError={apiKeyError}
			setApiKeyError={setApiKeyError}
			webhookSecretError={webhookSecretError}
			setWebhookSecretError={setWebhookSecretError}
			publishableKeyError={publishableKeyError}
			setPublishableKeyError={setPublishableKeyError}
			onSubmit={handleSubmit}
			isLoading={isLoading}
			error={error}
		/>
	)
}
